import React, { HTMLAttributes } from 'react';
import { Status, ApplicationStatus, BankStatus, PaymentStatus } from '../../types/common';

// Extended variant type — 'warm' is Badge-specific (not a global Status)
// Used for decorative trust/security badges that should feel distinct from
// functional status badges (approved, passed, etc.)
type BadgeVariant = Status | 'warm';

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  children: React.ReactNode;
}

export const Badge: React.FC<BadgeProps> = ({
  variant = 'neutral',
  size = 'md',
  className = '',
  children,
  style = {},
  ...props
}) => {
  const variantClasses: Record<BadgeVariant, string> = {
    primary: 'badge-primary',
    secondary: 'badge-neutral',
    success: 'badge-success',
    warning: 'badge-warning',
    danger: 'badge-danger',
    info: 'badge-info',
    neutral: 'badge-neutral',
    warm: 'badge-warm',
  };

  // Enhanced size classes with more noticeable differences  
  const sizeStyles: Record<string, React.CSSProperties> = {
    xs: {
      fontSize: '0.625rem',    // 10px
      padding: '0.125rem 0.375rem', // 2px 6px
      lineHeight: '1rem'
    },
    sm: {
      fontSize: '0.75rem',    // 12px
      padding: '0.125rem 0.5rem', // 2px 8px
      lineHeight: '1.25rem'
    },
    md: {
      fontSize: '0.875rem',    // 14px (default)
      padding: '0.25rem 0.75rem', // 4px 12px (default badge padding)
      lineHeight: '1.25rem'
    },
    lg: {
      fontSize: '1rem',        // 16px
      padding: '0.375rem 1rem', // 6px 16px
      lineHeight: '1.5rem'
    }
  };

  const classes = [variantClasses[variant], className]
    .filter(Boolean)
    .join(' ');

  return (
    <span 
      className={classes} 
      style={{
        ...sizeStyles[size],
        ...style
      }}
      {...props}
    >
      {children}
    </span>
  );
};

// Application Status Badge (Maps to: WF-APP-LIFECYCLE)
export interface ApplicationStatusBadgeProps {
  status: ApplicationStatus;
  className?: string;
}

export const ApplicationStatusBadge: React.FC<ApplicationStatusBadgeProps> = ({
  status,
  className = '',
}) => {
  const statusConfig: Record<ApplicationStatus, { variant: Status; label: string }> = {
    draft: { variant: 'neutral', label: 'Draft' },
    pending_payment: { variant: 'warning', label: 'Pending Payment' },
    credit_assessment: { variant: 'warning', label: 'Credit Assessment' },
    credit_passed: { variant: 'success', label: 'Credit Passed' },
    credit_failed: { variant: 'danger', label: 'Credit Failed' },
    premium_submission: { variant: 'info', label: 'Premium Submission' },
    submitted: { variant: 'info', label: 'Submitted' },
    distributed: { variant: 'info', label: 'Distributed' },
    in_review: { variant: 'warning', label: 'In Review' },
    approved: { variant: 'success', label: 'Pre-Qualified' },
    rejected: { variant: 'danger', label: 'Rejected' },
    conditional_approval: { variant: 'warning', label: 'Conditional Pre-Qualification' },
    withdrawn: { variant: 'neutral', label: 'Withdrawn' },
  };

  const config = statusConfig[status];

  return (
    <span className={`badge badge-${config.variant} ${className}`}>
      {config.label.toUpperCase()}
    </span>
  );
};

// Bank Status Badge (Maps to: WF-BANK-LIFECYCLE)
export interface BankStatusBadgeProps {
  status: BankStatus;
  className?: string;
}

export const BankStatusBadge: React.FC<BankStatusBadgeProps> = ({
  status,
  className = '',
}) => {
  const statusConfig: Record<BankStatus, { variant: Status; label: string }> = {
    pending: { variant: 'warning', label: 'Pending' },
    approved: { variant: 'success', label: 'Pre-Qualified' },
    rejected: { variant: 'danger', label: 'Rejected' },
    conditional_approval: { variant: 'warning', label: 'Conditional Pre-Qualification' },
    documents_requested: { variant: 'info', label: 'Documents Requested' },
    withdrawn: { variant: 'neutral', label: 'Withdrawn' },
  };

  const config = statusConfig[status];

  return (
    <span className={`badge badge-${config.variant} ${className}`}>
      {config.label.toUpperCase()}
    </span>
  );
};

// Payment Status Badge (Maps to: WF-PAYMENT-LIFECYCLE)
export interface PaymentStatusBadgeProps {
  status: PaymentStatus;
  className?: string;
}

export const PaymentStatusBadge: React.FC<PaymentStatusBadgeProps> = ({
  status,
  className = '',
}) => {
  const statusConfig: Record<PaymentStatus, { variant: Status; label: string }> = {
    pending: { variant: 'warning', label: 'Pending' },
    processing: { variant: 'info', label: 'Processing' },
    completed: { variant: 'success', label: 'Completed' },
    failed: { variant: 'danger', label: 'Failed' },
    refunded: { variant: 'neutral', label: 'Refunded' },
  };

  const config = statusConfig[status];

  return (
    <span className={`badge badge-${config.variant} ${className}`}>
      {config.label.toUpperCase()}
    </span>
  );
};